import React, { useCallback, useEffect, useState } from 'react';
import { Activity, Database, RefreshCw, AlertTriangle, CheckCircle2, Clock, Trash2 } from 'lucide-react';
import { monitorApi } from '../api';

const STATUS = {
  ok: { cls: 'text-emerald-600 bg-emerald-50', Icon: CheckCircle2, label: 'OK' },
  error: { cls: 'text-red-600 bg-red-50', Icon: AlertTriangle, label: 'Errore' },
  idle: { cls: 'text-paelbi-ink/50 bg-paelbi-bg2', Icon: Clock, label: 'Inattivo' },
};

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString('it-IT') : '—');

function Stat({ label, value, testid }) {
  return (
    <div className="rounded-2xl border border-paelbi-line bg-white p-5" data-testid={testid}>
      <p className="text-xs font-semibold uppercase tracking-wide text-paelbi-ink/50">{label}</p>
      <p className="mt-1 font-display text-3xl text-paelbi-ink">{value}</p>
    </div>
  );
}

export function Monitor() {
  const [data, setData] = useState(null);
  const [logs, setLogs] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const [o, l] = await Promise.all([monitorApi.overview(), monitorApi.logs({ limit: 30 })]);
    setData(o);
    setLogs(l);
  }, []);
  useEffect(() => { load(); }, [load]);

  const clearCache = async () => {
    if (!window.confirm('Svuotare tutta la cache dei provider? Le prossime richieste rieseguiranno le sincronizzazioni.')) return;
    setBusy(true);
    try { await monitorApi.clearCache(); await load(); } finally { setBusy(false); }
  };

  if (!data) return <p className="text-sm text-paelbi-ink/50">Caricamento centro di controllo...</p>;

  return (
    <div data-testid="monitor-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 font-display text-2xl text-paelbi-ink sm:text-3xl"><Activity size={24} /> Centro di controllo</h1>
          <p className="text-paelbi-ink/60">Provider, cache, sincronizzazioni ed errori della piattaforma.</p>
        </div>
        <div className="flex gap-2">
          <button type="button" data-testid="monitor-refresh" onClick={load} className="inline-flex items-center gap-2 rounded-btn border border-paelbi-line px-4 py-2 text-sm"><RefreshCw size={15} /> Aggiorna</button>
          <button type="button" data-testid="monitor-clear-cache" onClick={clearCache} disabled={busy} className="inline-flex items-center gap-2 rounded-btn border border-red-200 px-4 py-2 text-sm text-red-600 disabled:opacity-50"><Trash2 size={15} /> Svuota cache</button>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Stat label="Provider attivi" value={data.providers.length} testid="stat-providers" />
        <Stat label="Voci in cache" value={data.cache.entries} testid="stat-cache" />
        <Stat label="Sync (24h)" value={data.sync.runs_24h} testid="stat-runs" />
        <Stat label="Errori (24h)" value={data.sync.errors_24h} testid="stat-errors" />
      </div>

      {(data.alerts || []).length > 0 && (
        <div className="mt-6 rounded-2xl border border-red-200 bg-red-50 p-4" data-testid="monitor-alerts">
          <p className="flex items-center gap-2 font-semibold text-red-700"><AlertTriangle size={18} /> Attenzione: provider con errori ripetuti (24h)</p>
          <ul className="mt-2 space-y-1 text-sm text-red-600">
            {data.alerts.map((a) => (
              <li key={a.provider} data-testid={`alert-${a.provider}`}>{a.name}: {a.errors_24h} errori nelle ultime 24h</li>
            ))}
          </ul>
        </div>
      )}

      <h2 className="mt-8 font-display text-lg text-paelbi-ink">Provider</h2>
      <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="providers-list">
        {data.providers.map((p) => {
          const s = STATUS[p.status] || STATUS.idle;
          return (
            <div key={p.id} className={`rounded-2xl border bg-white p-4 ${p.alert ? 'border-red-300 ring-1 ring-red-200' : 'border-paelbi-line'}`} data-testid={`provider-${p.id}`}>
              <div className="flex items-center justify-between">
                <p className="font-medium text-paelbi-ink">{p.name}</p>
                <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ${s.cls}`}><s.Icon size={13} /> {s.label}</span>
              </div>
              <p className="mt-2 text-xs text-paelbi-ink/50">Ultima sync: {fmtDate(p.last_run)}</p>
              <p className="text-xs text-paelbi-ink/50">Elementi: {p.last_count ?? '—'} · {p.last_duration_ms ? `${p.last_duration_ms} ms` : '—'}</p>
              {p.last_error && <p className="mt-1 truncate text-xs text-red-500" title={p.last_error}>{p.last_error}</p>}
            </div>
          );
        })}
      </div>

      <h2 className="mt-8 font-display text-lg text-paelbi-ink">Contenuti per modulo</h2>
      <div className="mt-3 overflow-hidden rounded-2xl border border-paelbi-line bg-white" data-testid="counts-list">
        <div className="grid grid-cols-4 gap-2 border-b border-paelbi-line bg-paelbi-bg/50 px-4 py-2 text-xs font-semibold uppercase text-paelbi-ink/50">
          <span>Modulo</span><span>Globale</span><span>Custom host</span><span>Cache</span>
        </div>
        {data.modules.map((m) => (
          <div key={m.id} className="grid grid-cols-4 gap-2 border-b border-paelbi-line px-4 py-3 text-sm last:border-0" data-testid={`count-${m.id}`}>
            <span className="font-medium text-paelbi-ink">{m.name}</span>
            <span>{data.catalog_counts[m.id]?.global ?? 0}</span>
            <span>{data.catalog_counts[m.id]?.custom ?? 0}</span>
            <span>{data.cache.by_module[m.id] ?? (m.has_providers ? 0 : '—')}</span>
          </div>
        ))}
      </div>

      <h2 className="mt-8 flex items-center gap-2 font-display text-lg text-paelbi-ink"><Database size={18} /> Log operativi</h2>
      <div className="mt-3 overflow-x-auto rounded-2xl border border-paelbi-line bg-white" data-testid="logs-list">
        <table className="w-full text-sm">
          <thead className="bg-paelbi-bg/50 text-xs uppercase text-paelbi-ink/50">
            <tr><th className="px-4 py-2 text-left">Quando</th><th className="px-4 py-2 text-left">Provider</th><th className="px-4 py-2 text-left">Modulo</th><th className="px-4 py-2 text-left">Stato</th><th className="px-4 py-2 text-left">Elementi</th><th className="px-4 py-2 text-left">Durata</th></tr>
          </thead>
          <tbody>
            {logs.length === 0 && <tr><td colSpan={6} className="px-4 py-4 text-paelbi-ink/50">Nessun log disponibile.</td></tr>}
            {logs.map((l, i) => (
              <tr key={`${l.provider}-${l.ts}-${i}`} className="border-t border-paelbi-line">
                <td className="px-4 py-2 text-paelbi-ink/70">{fmtDate(l.ts)}</td>
                <td className="px-4 py-2">{l.provider}</td>
                <td className="px-4 py-2">{l.module}</td>
                <td className="px-4 py-2"><span className={l.status === 'error' ? 'text-red-600' : 'text-emerald-600'}>{l.status}</span></td>
                <td className="px-4 py-2">{l.item_count}</td>
                <td className="px-4 py-2">{l.duration_ms} ms</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default Monitor;
